// src/lib/time/signoff-rules.ts
import { formatOpensOn, type IsoDate, todayInZone, weekSignOffDueInstant } from "@/lib/time/dates";
import { normalizeComment } from "@/lib/time/rules";

/**
 * Weekly and monthly sign-off rules (TE-40..TE-50 with the 2026-10-15 decisions). Pure; the
 * server checks them before the submit write (whose WHERE repeats them) and the popup uses
 * them to enable buttons.
 */

export interface SignOffLike {
  id: string;
  periodType: "week" | "month";
  start: IsoDate;
  end: IsoDate;
  status: "Open" | "SignedOff";
  /** DueOnDate on the warehouse's UTC clock, ISO with or without a trailing Z. */
  dueOn: string;
  isLate: boolean;
  hourCapacity: number | null;
  /** Every active entry in the period, PTO included (TE-42). */
  totalHours: number;
}

export interface ReasonLike {
  id: string;
  name: string;
  commentRequired: boolean;
}

export interface SignOffChoice {
  reasonId: string | null;
  comment: string | null;
}

export function isEnded(row: Pick<SignOffLike, "end">, today: IsoDate): boolean {
  return row.end < today;
}

export function dueInstant(dueOn: string): number {
  return Date.parse(dueOn.endsWith("Z") ? dueOn : `${dueOn}Z`);
}

/**
 * When a row is due as a UTC instant. Weeks are late after Monday 2:00 PM Eastern (Zack,
 * 2026-10-15), computed from the period end, never from the stored DueOnDate. Months keep
 * the stored due date.
 */
function rowDueInstant(row: SignOffLike): number {
  return row.periodType === "week"
    ? weekSignOffDueInstant(row.end).getTime()
    : dueInstant(row.dueOn);
}

/** TE-45/48: stored IsLate, or still open after the due time. */
export function isLateNow(row: SignOffLike, now: Date): boolean {
  return row.isLate || (row.status === "Open" && now.getTime() > rowDueInstant(row));
}

export function missingHours(row: Pick<SignOffLike, "hourCapacity" | "totalHours">): number {
  const missing = (row.hourCapacity ?? 0) - row.totalHours;
  return missing > 0 ? Math.round(missing * 100) / 100 : 0;
}

/** TE-43: full-time staff explain missing hours; part-time and variable schedules do not. */
export function needsReason(row: SignOffLike, isFullTime: boolean): boolean {
  return isFullTime && missingHours(row) > 0;
}

/** Weeks belong to the month they end in (TE-35/40). */
function weeksOfMonth(month: SignOffLike, rows: SignOffLike[]): SignOffLike[] {
  return rows.filter((r) => r.periodType === "week" && r.end >= month.start && r.end <= month.end);
}

/** What a month says until every week ending in it is signed off. */
export const MONTH_NEEDS_WEEKS = "Sign off every week first";

export function signOffBlock(
  row: SignOffLike,
  choice: SignOffChoice,
  ctx: { isFullTime: boolean; reasons: ReasonLike[]; today: IsoDate; rows: SignOffLike[] },
): string | null {
  if (row.status !== "Open") return "Already signed off";
  if (row.start > ctx.today) return `Opens ${formatOpensOn(row.start)}`;
  if (row.periodType === "month") {
    const weeks = weeksOfMonth(row, ctx.rows);
    if (weeks.length === 0 || weeks.some((w) => w.status !== "SignedOff")) {
      return MONTH_NEEDS_WEEKS;
    }
  }
  // Nothing missing: a stray reason or comment is ignored.
  if (missingHours(row) === 0) return null;
  const wanted = choice.reasonId?.toUpperCase() ?? null;
  const reason = wanted ? ctx.reasons.find((r) => r.id.toUpperCase() === wanted) : undefined;
  if (wanted && !reason) return "Choose a reason";
  if (needsReason(row, ctx.isFullTime) && !reason) return "Choose a reason";
  // Missing hours always need a comment, whatever the reason (TE-43).
  const commentNeeded = needsReason(row, ctx.isFullTime) || reason?.commentRequired === true;
  if (commentNeeded && normalizeComment(choice.comment) === null) return "Add a comment";
  return null;
}

/** Ended weeks still open, oldest first: the ones that are owed, so what the alerts count. */
export function openPastWeeks<T extends SignOffLike>(rows: T[], today: IsoDate): T[] {
  return rows
    .filter((r) => r.periodType === "week" && r.status === "Open" && isEnded(r, today))
    .sort((a, b) => a.end.localeCompare(b.end));
}

/** The popup's week list: every open week that has started, oldest first, so the current week is last. */
export function signableWeeks<T extends SignOffLike>(rows: T[], today: IsoDate): T[] {
  return rows
    .filter((r) => r.periodType === "week" && r.status === "Open" && r.start <= today)
    .sort((a, b) => a.end.localeCompare(b.end));
}

/**
 * The popup's month list: every open month that has started and has weeks, oldest first. A month
 * stays listed while its weeks are unsigned, so people can see it is still to do. A row with no
 * weeks can never be signed off, so it stays hidden.
 */
export function openMonths<T extends SignOffLike>(rows: T[], today: IsoDate): T[] {
  return rows
    .filter((r) => r.periodType === "month" && r.status === "Open" && r.start <= today)
    .filter((m) => weeksOfMonth(m, rows).length > 0)
    .sort((a, b) => a.end.localeCompare(b.end));
}

/** A listed month whose weeks are all signed off, so it can be signed off itself. */
export function hasReadyMonth(rows: SignOffLike[], today: IsoDate): boolean {
  return openMonths(rows, today).some((m) =>
    weeksOfMonth(m, rows).every((w) => w.status === "SignedOff"),
  );
}

/**
 * TE-50 for the current year (America/New_York): periods due this year that are signed off
 * and not late, over periods already due. Both sides come from the same already-due rows, so
 * the result never exceeds 100. Submitted rows use the stored due date and IsLate; open weeks
 * use the Amendment 1 due instant. Null when nothing is due yet.
 */
export function onTimePercent(rows: SignOffLike[], now: Date): number | null {
  const year = todayInZone(now).slice(0, 4);
  const dueRows = rows.filter((r) => {
    const due = r.status === "SignedOff" ? dueInstant(r.dueOn) : rowDueInstant(r);
    return due < now.getTime() && todayInZone(new Date(due)).slice(0, 4) === year;
  });
  if (dueRows.length === 0) return null;
  const onTime = dueRows.filter((r) => r.status === "SignedOff" && !r.isLate).length;
  return Math.round((onTime * 100) / dueRows.length);
}

/** The plan's allowance: the first 5 late sign-offs a year are free. */
export const FREE_LATE_SIGNOFFS = 5;

/**
 * Late sign-offs (weekly and monthly) due in `year` (America/New_York): only those the warehouse
 * stored as late (`isLate`), which is what the Late Time Entry penalty is computed from. An open
 * row past due is not counted. Due year is decided as in `onTimePercent`.
 */
export function lateSignOffCount(rows: SignOffLike[], year: number): number {
  return rows.filter((r) => {
    const due = r.status === "SignedOff" ? dueInstant(r.dueOn) : rowDueInstant(r);
    return todayInZone(new Date(due)).slice(0, 4) === String(year) && r.isLate;
  }).length;
}

/** The plan's late-submission reduction: 1% for each one after the first 5, cumulative. */
export function lateSignOffPenalty(count: number): number {
  return Math.max(0, count - FREE_LATE_SIGNOFFS) / 100;
}

/** A sign-off row with its month's time-entry lock (Period.TimeEntryClosed). */
export type RevocableLike = SignOffLike & { monthClosed: boolean };

/**
 * TE-46/47: the person can revoke their own signed-off week or month until its month is closed.
 * Vinyl has no month rule: a week can be revoked while its month stays signed off.
 */
export function revokeBlock(row: RevocableLike): string | null {
  if (row.status !== "SignedOff") return "Not signed off";
  if (row.monthClosed) return "Month is closed";
  return null;
}

/** Rows the Sign-off dialog offers to revoke: newest first, a month before its last week. */
export function revocableRows<T extends RevocableLike>(rows: T[]): T[] {
  return rows
    .filter((r) => revokeBlock(r) === null)
    .sort((a, b) => {
      if (a.end !== b.end) return b.end.localeCompare(a.end);
      if (a.periodType !== b.periodType) return a.periodType === "month" ? -1 : 1;
      return a.id.localeCompare(b.id);
    });
}

/**
 * The signed-off, revocable month a signed-off week ends in, or null (controller decision
 * 2026-10-16). Revoking such a week alone leaves its entries locked by the month, so the week
 * is revoked together with its month, month first.
 */
export function signedOffMonthOf<T extends RevocableLike>(
  week: RevocableLike,
  rows: T[],
): T | null {
  if (week.periodType !== "week") return null;
  return (
    rows.find(
      (r) =>
        r.periodType === "month" &&
        r.start <= week.end &&
        week.end <= r.end &&
        revokeBlock(r) === null,
    ) ?? null
  );
}
